import React, { useState, useCallback, useRef, useEffect } from 'react';
import { RoomTypeSelector, PlatformSelector, VideoModeSelector } from './steps';
import './QuestionWizard.css';

interface WizardStep {
  id: string;
  title: string;
  subtitle: string;
  component: React.ComponentType<any>;
}

interface QuestionWizardProps {
  onComplete: (answers: string[]) => void;
}


const wizardSteps: WizardStep[] = [
  {
    id: 'room-type',
    title: 'WHAT TYPE OF ROOM ARE YOU SETTING UP?',
    subtitle: 'Choose the size of your room for the best recommendations',
    component: RoomTypeSelector
  },
  {
    id: 'platform-selection',
    title: 'WHICH PLATFORM WILL YOU BE USING?',
    subtitle: "We'll recommend the best equipment based on the platform",
    component: PlatformSelector
  },
  {
    id: 'video-mode',
    title: 'WHAT TYPE OF VIDEO MODE DO YOU PREFER?',
    subtitle: 'Video mode will optimize the camera for your needs',
    component: VideoModeSelector
  }
];

export const QuestionWizard: React.FC<QuestionWizardProps> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [currentStep]);

  const handleStepSelect = useCallback((value: string, shouldAutoAdvance: boolean = true) => {
    const newAnswers = [...answers];
    newAnswers[currentStep] = value;
    setAnswers(newAnswers);

    // If step specifically says not to auto-advance, just save the answer
    if (!shouldAutoAdvance) {
      return;
    }

    // Start transition animation
    setIsTransitioning(true);

    // Auto-advance to next step or complete immediately
    if (currentStep < wizardSteps.length - 1) {
      setCurrentStep(currentStep + 1);
      setIsTransitioning(false);
    } else {
      // Complete the wizard
      onComplete(newAnswers);
    }
  }, [currentStep, answers, onComplete]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleNext = useCallback(() => {
    if (currentStep < wizardSteps.length - 1) {
      // If no answer selected, use "Not Sure"
      if (!answers[currentStep]) {
        handleStepSelect('Not Sure');
      } else {
        setCurrentStep(currentStep + 1);
      }
    } else {
      // Complete wizard
      const finalAnswers = [...answers];
      if (!finalAnswers[currentStep]) {
        finalAnswers[currentStep] = 'Not Sure';
      }
      onComplete(finalAnswers);
    }
  }, [currentStep, answers, handleStepSelect, onComplete]);


  const handleProgressDotClick = useCallback((stepIndex: number) => {
    if (stepIndex !== currentStep && !isTransitioning) {
      // If jumping ahead, fill in "Not Sure" for any skipped stages
      if (stepIndex > currentStep) {
        const newAnswers = [...answers];
        for (let i = currentStep; i < stepIndex; i++) {
          if (!newAnswers[i]) {
            newAnswers[i] = 'Not Sure';
          }
        }
        setAnswers(newAnswers);
      }
      setCurrentStep(stepIndex);
    }
  }, [currentStep, isTransitioning, answers]);

  const currentStepData = wizardSteps[currentStep];
  const CurrentStepComponent = currentStepData.component;

  return (
    <div className="question-wizard-overlay">
      <div className="question-wizard">
        <div className="wizard-header">
          <div className="wizard-tag">ROOM BUNDLE FINDER</div>
          <h1 className="wizard-title">{currentStepData.title}</h1>
          <p className="wizard-subtitle">{currentStepData.subtitle}</p>
        </div>

        <div ref={contentRef} className={`wizard-content ${isTransitioning ? 'transitioning' : ''}`}>
          <CurrentStepComponent onSelect={handleStepSelect} />
        </div>

        <div className="wizard-navigation">
          <div className="wizard-progress">
            {wizardSteps.map((_, index) => (
              <button
                key={index}
                className={`progress-dot ${
                  index === currentStep 
                    ? 'active' 
                    : index < currentStep 
                    ? 'completed' 
                    : ''
                }`}
                onClick={() => handleProgressDotClick(index)}
                disabled={isTransitioning}
              />
            ))}
          </div>

          <div className="wizard-nav-actions">
            <button
              className="wizard-nav-button wizard-back-button"
              onClick={handleBack}
              disabled={currentStep === 0 || isTransitioning}
            >
              <span>←</span>
              BACK
            </button>

            <button
              className="wizard-nav-button primary wizard-next-button"
              onClick={handleNext}
              disabled={isTransitioning}
            >
              {currentStep === wizardSteps.length - 1 ? 'GET RECS' : 'NEXT'}
              <span>→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
